import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useLocale, useModalState, useMutation, useQueryClient } from 'shared-ui';

import { getLicense, saveLicense } from 'api/license/license';
import { QueryKeyEnum } from 'core/global.enum';
import { useQuery } from 'hooks/useQuery/useQuery';
import { AppMessages } from 'i18n/messages';

import { License } from './License';
import { AddLicenseModal } from './addLicenseModal/AddLicenseModal';

export const LicenseContainer = () => {
  const { formatMessage } = useLocale();
  const queryClient = useQueryClient();
  const { isModalOpen, onModalClose, onModalOpen } = useModalState();
  const [searchParams, setSearchParams] = useSearchParams();
  const hasOpenedModalRef = useRef(false);

  useEffect(() => {
    if (searchParams.get('openAddLicense') === 'true' && !hasOpenedModalRef.current) {
      hasOpenedModalRef.current = true;
      onModalOpen();
      searchParams.delete('openAddLicense');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, onModalOpen]);
  const { data: license } = useQuery([QueryKeyEnum.LICENSE], getLicense, {
    handleErrorManually: true,
  });

  const { mutate: onSaveLicense, isLoading: isSaving } = useMutation(saveLicense, {
    onSuccess: () => {
      // Clear invalid license status and reload to get fresh permissions
      localStorage.removeItem('licenseStatus');
      sessionStorage.removeItem('licenseStatus');
      queryClient.invalidateQueries([QueryKeyEnum.LICENSE]);
      onModalClose();
      toast.dark(formatMessage({ id: AppMessages['license.modal.success'] }));
      // Reload to refresh permissions with valid license
      window.location.reload();
    },
  });

  const handleSubmitLicense = (licenseKey: string) => {
    onSaveLicense({ licenseKey });
  };

  return (
    <>
      <License license={license ?? null} onAddLicense={onModalOpen} />
      <AddLicenseModal
        isOpen={isModalOpen}
        onClose={onModalClose}
        onSubmit={handleSubmitLicense}
        isSubmitting={isSaving}
      />
    </>
  );
};
